import { BreakpointState } from '../../__styling/style-functions/breakpoint'
import {
    calcRem,
    PartialComponentTheme,
    PerthNowTheme,
    PerthNowThemeConfig,
    SevenNewsTheme,
    SevenNewsThemeConfig,
    ThemeConfig,
    TheNightlyTheme,
    TheNightlyThemeConfig,
    TheWestTheme,
    TheWestThemeConfig,
} from '../../__styling'
import { TextTransformProperty } from 'csstype'

export type SectionHeaderTheme<T extends ThemeConfig> = {
    background?: string
    padding?: BreakpointState<T['breakpoints'], string>
    minHeight?: string
    heading?: {
        textType?: BreakpointState<T['breakpoints'], T['textType']>
        textTransform?: TextTransformProperty
        textColor?: string
        padding?: string
        verticalGap?: string
        active?: {
            background: string
        }
        hover?: {
            background: string
        }
    }
    chevron?: {
        color: string
        marginLeft: string
    }
    /** Divider underneath the heading. */
    divider: {
        width: string
        height: string
        color: string
    } | null
    readMore: {
        textType: T['textType']
        gap: string
    } | null
    nav?: {
        padding?: string
        link: {
            textType: T['textType']
            textColor: string
            padding?: string
            active?: {
                background: string
            }
            hover?: {
                background: string
            }
        }
        divider: {
            height: string
            color: string
        }
    }
}

export const perthNowSectionHeaderTheme = (scheme: PerthNowTheme) => {
    const theme: SectionHeaderTheme<PerthNowThemeConfig> = {
        background: scheme.sectionValues.primaryColor,
        padding: { default: calcRem(0, 2) },
        minHeight: calcRem(36),
        heading: {
            textType: { default: 'head-6' },
            textTransform: 'uppercase',
            textColor: scheme.colors.palette.white,
            padding: calcRem(4, 8),
            hover: {
                background: 'rgba(0,0,0,0.5)',
            },
            active: {
                background: 'rgba(243, 147, 147, 0.2)',
            },
        },
        chevron: {
            color: scheme.colors.palette.white,
            marginLeft: calcRem(scheme.margins.xs),
        },
        divider: null,
        readMore: null,
        nav: {
            padding: calcRem(0, 3, 0, 0),
            link: {
                textType: 'semi-bold/14',
                textColor: scheme.colors.palette.white,
                padding: calcRem(4),
                hover: {
                    background: 'rgba(0,0,0,0.5)',
                },
                active: {
                    background: 'rgba(255,255,255,0.2)',
                },
            },
            divider: {
                color: scheme.colors.palette.white,
                height: calcRem(12),
            },
        },
    }
    return theme
}

export const perthNowSponsoredSectionHeaderOverride = (
    scheme: PerthNowTheme,
) => {
    const theme: PartialComponentTheme<PerthNowThemeConfig> = {
        sectionHeader: {
            background: 'transparent',
            padding: { default: calcRem(0) },
            minHeight: calcRem(33),
            heading: {
                textType: { default: 'head-5' },
                textColor: scheme.colors.text.heading,
                verticalGap: calcRem(4),
                padding: 'unset',
            },
            divider: {
                width: '100%',
                height: calcRem(4),
                color: scheme.colors.text.heading,
            },
        },
    }
    return theme
}

export const sevenNewsSectionHeaderTheme: SectionHeaderTheme<SevenNewsThemeConfig> =
    {
        heading: {
            textType: { default: 'header' },
            textTransform: 'none',
        },
        divider: null,
        readMore: null,
    }

export const theWestSectionHeaderTheme = (scheme: TheWestTheme) => {
    const theme: SectionHeaderTheme<TheWestThemeConfig> = {
        heading: {
            textType: { default: 'head-5' },
            textTransform: 'uppercase',
            textColor: scheme.colors.text.secondary,
            verticalGap: calcRem(4),
        },
        divider: {
            width: calcRem(31),
            height: calcRem(3),
            color: scheme.colors.palette.black,
        },
        readMore: null,
        chevron: {
            color: scheme.colors.text.secondary,
            marginLeft: calcRem(scheme.margins.xs),
        },
        nav: {
            link: {
                textType: 'medium/14',
                textColor: scheme.colors.text.tertiary ?? 'unset',
            },
            divider: {
                color: scheme.colors.borders.primary,
                height: calcRem(16),
            },
        },
    }
    return theme
}

export const theNightlySectionHeaderTheme = (scheme: TheNightlyTheme) => {
    const theme: SectionHeaderTheme<TheNightlyThemeConfig> = {
        background: `linear-gradient(0deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.96) 100%), ${scheme.sectionValues.primaryColor}`,
        padding: {
            default: calcRem(32, scheme.margins.sm, scheme.margins.sm),
            sm: calcRem(48, scheme.margins.md, scheme.margins.md),
            lg: calcRem(64, scheme.margins.md, scheme.margins.md),
        },
        divider: null,
        readMore: {
            textType: 'body-alt',
            gap: calcRem(scheme.margins.xs),
        },
        heading: {
            textType: { default: 'section-header' },
            textTransform: 'capitalize',
            textColor: scheme.sectionValues.primaryColor,
            verticalGap: calcRem(scheme.margins.xs),
        },
    }
    return theme
}

export const theNightlySmallSectionHeaderOverride: PartialComponentTheme<TheNightlyThemeConfig> =
    {
        sectionHeader: {
            background: 'unset',
            padding: {
                default: 'unset',
                sm: 'unset',
                lg: 'unset',
            },
            readMore: null,
            heading: {
                textType: {
                    default: 'section-header',
                    lg: 'subheading',
                },
            },
        },
    }

export const aflMatchCentreSectionHeaderOverride: PartialComponentTheme<TheWestThemeConfig> =
    {
        sectionHeader: {
            divider: {
                width: '100%',
            },
        },
    }
